import { AccessControlled } from '@/components/access-control'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { AccessControlInput } from '@/hooks/access-control'
import { useQuotaUsageTooltip } from '@/hooks/useQuotaUsageTooltip'
import { ChevronDown, X } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelectionPopoverHold } from './selectionPopoverHold'
import type { DropdownActionItem, SelectionAction, SelectionToolbarProps } from './types'

/**
 * Wraps `node` in `<AccessControlled>` when `access` is set; otherwise returns it as-is.
 * Used to keep the rest of the toolbar's branching unaware of access-control wiring.
 */
const withAccessControl = (node: React.ReactElement, access: AccessControlInput | undefined): React.ReactElement => {
	if (!access) {
		return node
	}
	return <AccessControlled {...access}>{node}</AccessControlled>
}

const withAccessControlDropdownMenuItem = (
	node: React.ReactElement,
	access: AccessControlInput | undefined,
): React.ReactElement => {
	if (!access) {
		return node
	}
	return <AccessControlled {...access}>{node}</AccessControlled>
}

/** Props injected via cloneElement into action/item components rendered inside popovers. */
interface PopoverInjectedProps {
	onClose?: (...args: unknown[]) => void
	onSuccess?: (...args: unknown[]) => void
	[key: string]: unknown
}

interface PopoverPosition {
	x: number
	y: number
	positionAbove?: boolean
	maxHeight?: number
}

// Helper to check if click target is on a radix/select element (rendered in portals)
const isRadixPortalElement = (target: Element): boolean => {
	return !!(
		target.closest('[role="dialog"]') ||
		target.closest('[data-radix-popper-content-wrapper]') ||
		target.closest('[data-radix-dropdown-menu-trigger]') ||
		target.closest('[data-radix-dropdown-menu-content]') ||
		target.closest('[data-radix-select-trigger]') ||
		target.closest('[data-radix-select-content]') ||
		target.closest('[data-slot="select-content"]') ||
		target.closest('[data-slot="select-trigger"]') ||
		target.closest('[data-slot="select-item"]') ||
		target.closest('[role="listbox"]') ||
		target.closest('[role="option"]')
	)
}

// Modal Radix layers (Select, Dialog) set `body.pointerEvents = 'none'` while open.
const isModalRadixLayerOpen = (): boolean => document.body.style.pointerEvents === 'none'

// Helper to check if click target should prevent popover/toolbar close
const shouldPreventClose = (target: Element): boolean => {
	return (
		!document.body.contains(target) ||
		isRadixPortalElement(target) ||
		!!target.closest('[data-prevent-close]') ||
		// <html>/<body> target = click leaked through a modal layer (pointer-events:none
		// makes the trigger non-clickable); not a real outside click.
		target === document.documentElement ||
		target === document.body ||
		isModalRadixLayerOpen()
	)
}

// Helper to check if click target is an interactive element (buttons, links, inputs, etc.)
const isInteractiveElement = (target: Element): boolean => {
	const interactiveSelectors = [
		'button',
		'a',
		'input',
		'select',
		'textarea',
		'[role="button"]',
		'[role="link"]',
		'[role="menuitem"]',
		'[role="tab"]',
		'[tabindex]:not([tabindex="-1"])',
		'[data-clickable="true"]',
	]
	return interactiveSelectors.some(selector => target.closest(selector))
}

const BOTTOM_BAR_HEIGHT = 44 // --height-bottom-status-bar
const VIEWPORT_PADDING = 10
const ESTIMATED_POPOVER_HEIGHT = 500

// Max ids rendered in the "Selected: N" tooltip — select-all can hold 100k+ ids.
const MAX_TOOLTIP_IDS = 50

const computePopoverPosition = (rect: DOMRect) => {
	const viewportHeight = window.innerHeight
	const usableSpaceBelow = Math.max(viewportHeight - rect.bottom - BOTTOM_BAR_HEIGHT - VIEWPORT_PADDING, 0)
	const usableSpaceAbove = Math.max(rect.top - VIEWPORT_PADDING, 0)
	// Position above only if space below is insufficient and above has strictly more space (defaults to below on tie)
	const shouldPositionAbove = usableSpaceBelow < ESTIMATED_POPOVER_HEIGHT && usableSpaceAbove > usableSpaceBelow
	return {
		x: rect.left,
		y: shouldPositionAbove ? rect.top - 8 : rect.bottom + 8,
		positionAbove: shouldPositionAbove,
		maxHeight: shouldPositionAbove ? usableSpaceAbove : usableSpaceBelow,
	}
}

// Helper component to wrap actions with tooltips. Vertical dropdown-menu rows must pass
// `wrapperClassName='block'` — inline-block can end up sharing a line with the next row.
const TooltipWrapper: React.FC<{
	children: React.ReactNode
	tooltip?: string
	/** The action's own access config. Its quota spend, if any, is appended to `tooltip`. */
	access?: AccessControlInput
	dataTestId?: string
	side?: 'top' | 'bottom' | 'left' | 'right'
	wrapperClassName?: string
}> = ({ children, tooltip, access, dataTestId, side = 'top', wrapperClassName = 'inline-block' }) => {
	const resolvedTooltip = useQuotaUsageTooltip(tooltip, access)
	if (resolvedTooltip) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<div className={wrapperClassName}>{children}</div>
				</TooltipTrigger>
				<TooltipContent
					side={side}
					sideOffset={8}
					avoidCollisions={false}
					className='max-w-xs'
					data-testid={dataTestId}>
					<p>{resolvedTooltip}</p>
				</TooltipContent>
			</Tooltip>
		)
	}
	return <>{children}</>
}

const SingleButtonPopoverWrapper: React.FC<{
	action: SelectionAction
	selectedDropdownItem: string | null
	setSelectedDropdownItem: (id: string | null) => void
	setOpenDropdownId: (id: string | null) => void
	onClearSelection: () => void
	setActivePopoverItem: (item: DropdownActionItem | null) => void
}> = ({
	action,
	selectedDropdownItem,
	setSelectedDropdownItem,
	setOpenDropdownId,
	onClearSelection,
	setActivePopoverItem,
}) => {
	const [open, setOpen] = useState(false)
	useSelectionPopoverHold(open)

	const handleClose = () => {
		setOpen(false)
	}

	React.useEffect(() => {
		// If this is not the selected item, close this popover
		if (selectedDropdownItem !== action.id && open) {
			setOpen(false)
		}
		if (selectedDropdownItem === null && open && action.component) {
			setSelectedDropdownItem(action.id)
		}
	}, [selectedDropdownItem, action.id, open, action.component, setSelectedDropdownItem])

	if (!action.component) {
		return null
	}

	const button = (
		<Popover
			open={open}
			onOpenChange={newOpen => {
				setOpen(newOpen)
			}}>
			<PopoverTrigger asChild>
				<Button
					variant={action.variant || 'ghost'}
					onClick={e => {
						e.stopPropagation()
						setOpenDropdownId(null)
						setActivePopoverItem(null)
						setSelectedDropdownItem(action.id)
						// Use setTimeout to open after dropdown closes
						if (open) {
							setOpen(false)
						} else {
							setSelectedDropdownItem(action.id)
							setTimeout(() => {
								setOpen(true)
							}, 150)
						}
						action.onClick?.()
					}}
					disabled={action.disabled}
					data-testid={action.dataTestId}
					className={`px-2 text-body font-medium border hover:bg-sidebar rounded-sm transform transition-transform duration-200 ease-in-out hover:scale-105 data-[state=open]:scale-105 data-[state=open]:bg-sidebar ${
						action.disabled ? 'opacity-50 cursor-not-allowed' : 'text-foreground'
					} ${selectedDropdownItem === action.id ? 'bg-sidebar' : ''}`}>
					{action.icon && (
						<span className={action.disabled ? 'mr-2' : 'mr-2 [&_svg]:opacity-100 [&_svg]:text-foreground'}>
							{action.icon}
						</span>
					)}
					{action.label}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				side='bottom'
				align={
					['start', 'center', 'end'].includes(action?.align as string)
						? (action?.align as 'start' | 'center' | 'end')
						: 'start'
				}
				sideOffset={5}
				className='p-0 z-240 w-max max-h-[var(--radix-popover-content-available-height)] flex flex-col overflow-hidden'
				data-testid={action.popoverTestId}
				collisionPadding={{ top: 10, bottom: BOTTOM_BAR_HEIGHT + 10, left: 10, right: 10 }}
				avoidCollisions={true}
				onOpenAutoFocus={e => {
					e.preventDefault()
				}}
				onInteractOutside={e => {
					const target = e.target as Element
					// Prevent closing if clicking on dropdown triggers, menu items, select components,
					// or elements marked with data-prevent-close attribute
					if (shouldPreventClose(target) || target.closest('button')) {
						e.preventDefault()
					}
				}}>
				{React.isValidElement(action.component)
					? React.cloneElement(action.component as React.ReactElement<PopoverInjectedProps>, {
							onClose: (...args: unknown[]) => {
								const originalOnClose = (action.component as React.ReactElement<PopoverInjectedProps>).props?.onClose
								if (typeof originalOnClose === 'function') {
									originalOnClose(...args)
								}
								handleClose()
							},
							onSuccess: (...args: unknown[]) => {
								const originalOnSuccess = (action.component as React.ReactElement<PopoverInjectedProps>).props
									?.onSuccess
								if (typeof originalOnSuccess === 'function') {
									originalOnSuccess(...args)
								}
								onClearSelection()
							},
						})
					: action.component}
			</PopoverContent>
		</Popover>
	)

	return (
		<TooltipWrapper tooltip={action.tooltip} access={action.access} dataTestId={`${action.id}-tooltip`}>
			{button}
		</TooltipWrapper>
	)
}

const DropdownPopoverWrapper: React.FC<{
	item: DropdownActionItem
	closeParentDropdown: () => void
	onClearSelection: () => void
}> = ({ item, closeParentDropdown, onClearSelection }) => {
	const [popoverOpen, setPopoverOpen] = useState(false)
	useSelectionPopoverHold(popoverOpen)
	const buttonRef = React.useRef<HTMLDivElement>(null)
	const popoverRef = React.useRef<HTMLDivElement>(null)
	const positionRef = React.useRef<PopoverPosition | null>(null)

	const handleClose = useCallback(() => {
		setPopoverOpen(false)
		positionRef.current = null
	}, [])

	// Helper to open the popover based on an element (used by mouse and keyboard)
	const openPopoverAtElement = (targetElement: HTMLElement) => {
		if (item.disabled) {
			return
		}

		const rect = targetElement.getBoundingClientRect()
		positionRef.current = computePopoverPosition(rect)

		// Close dropdown
		closeParentDropdown()

		// Open popover - this will trigger re-render
		setTimeout(() => {
			setPopoverOpen(true)
		}, 200)
	}

	const handleItemClick = (e: React.MouseEvent<HTMLDivElement>) => {
		e.preventDefault()
		e.stopPropagation()
		openPopoverAtElement(e.currentTarget as HTMLElement)
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		// Support Enter and Space keys to open the same popover (accessibility)
		if (item.disabled) {
			return
		}
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			e.stopPropagation()
			openPopoverAtElement(e.currentTarget as HTMLElement)
		}
	}

	React.useEffect(() => {
		if (!popoverOpen) {
			return
		}

		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Element
			// Don't close if clicking on select/radix elements or elements marked to prevent close
			if (shouldPreventClose(target)) {
				return
			}
			if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
				handleClose()
			}
		}

		const timeoutId = setTimeout(() => {
			document.addEventListener('mousedown', handleClickOutside)
		}, 300)

		return () => {
			clearTimeout(timeoutId)
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [popoverOpen, handleClose])

	return (
		<>
			<TooltipWrapper
				tooltip={item.tooltip}
				access={item.access}
				dataTestId={`${item.id}-tooltip`}
				wrapperClassName='block'>
				<div
					ref={buttonRef}
					role='menuitem'
					className="focus:bg-sidebar focus:text-sidebar-accent-foreground relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-0.5 text-body! outline-hidden data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-(--icon-standalone) hover:bg-sidebar"
					onClick={handleItemClick}
					onKeyDown={handleKeyDown}
					tabIndex={item.disabled ? -1 : 0}
					data-disabled={item.disabled ? true : undefined}>
					{item.icon && (
						<span
							className={
								item.disabled
									? '[&_svg]:size-(--icon-standalone)'
									: '[&_svg]:size-(--icon-standalone) [&_svg]:opacity-100 [&_svg]:text-foreground'
							}>
							{item.icon}
						</span>
					)}
					<span>{item.label}</span>
				</div>
			</TooltipWrapper>

			{popoverOpen &&
				positionRef.current &&
				createPortal(
					<div
						ref={el => {
							if (popoverRef.current !== el) {
								popoverRef.current = el
							}
						}}
						className='fixed z-9999 bg-background border border-border shadow-xl rounded-md min-w-75 flex flex-col overflow-hidden'
						style={{
							left: `${positionRef.current.x}px`,
							...(positionRef.current.positionAbove
								? { bottom: `${window.innerHeight - positionRef.current.y}px` }
								: { top: `${positionRef.current.y}px` }),
							...(positionRef.current.maxHeight ? { maxHeight: `${positionRef.current.maxHeight}px` } : {}),
						}}>
						{React.isValidElement(item.component)
							? React.cloneElement(item.component as React.ReactElement<PopoverInjectedProps>, {
									onClose: (...args: unknown[]) => {
										const originalOnClose = (item.component as React.ReactElement<PopoverInjectedProps>).props?.onClose
										if (typeof originalOnClose === 'function') {
											originalOnClose(...args)
										}
										handleClose()
									},
									onSuccess: (...args: unknown[]) => {
										const originalOnSuccess = (item.component as React.ReactElement<PopoverInjectedProps>).props
											?.onSuccess
										if (typeof originalOnSuccess === 'function') {
											originalOnSuccess(...args)
										}
										onClearSelection()
									},
								})
							: item.component}
					</div>,
					document.body,
				)}
		</>
	)
}

const HoverDropdown: React.FC<{
	actions: DropdownActionItem[]
	label: string
	className?: string
	onClearSelection: () => void
}> = ({ actions, label, className = '', onClearSelection }) => {
	const [open, setOpen] = useState(false)

	return (
		<DropdownMenu open={open} onOpenChange={setOpen}>
			<DropdownMenuTrigger asChild>
				<Button
					variant='ghost'
					size='sm'
					className={`h-(--height-control) px-3 text-body font-medium text-foreground min-w-25 flex items-center justify-center border hover:bg-sidebar rounded-sm transform transition-transform duration-200 ease-in-out hover:scale-105 data-[state=open]:scale-105 data-[state=open]:bg-sidebar ${className}`}>
					{label}
					<ChevronDown className='ml-2 size-(--icon-standalone)' />
				</Button>
			</DropdownMenuTrigger>

			<DropdownMenuContent
				align='start'
				side='bottom'
				sideOffset={4}
				collisionPadding={20}
				avoidCollisions={true}
				className='w-auto min-w-50 z-[220]'>
				{actions.map(action => {
					const menuItem = action.component ? (
						<DropdownPopoverWrapper
							key={action.id}
							item={action}
							closeParentDropdown={() => setOpen(false)}
							onClearSelection={onClearSelection}
						/>
					) : (
						<DropdownMenuItem
							key={action.id}
							onSelect={async () => {
								if (!action.disabled) {
									await action.onClick?.()
									onClearSelection()
									setOpen(false)
								}
							}}
							disabled={action.disabled}
							className='flex items-center gap-2 cursor-pointer text-control! h-(--height-control) min-h-(--height-control)'>
							{action.icon && <span>{action.icon}</span>}
							<span>{action.label}</span>
						</DropdownMenuItem>
					)

					const itemWithTooltip = (
						<TooltipWrapper
							tooltip={action.tooltip}
							access={action.access}
							dataTestId={`${action.id}-tooltip`}
							wrapperClassName='block'>
							{menuItem}
						</TooltipWrapper>
					)

					return (
						<React.Fragment key={action.id}>
							{withAccessControlDropdownMenuItem(itemWithTooltip, action.access)}
						</React.Fragment>
					)
				})}
				{actions.length === 0 && (
					<DropdownMenuItem disabled className='text-control text-muted-foreground'>
						No actions available
					</DropdownMenuItem>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

const isDestructiveOrDropdown = (action: SelectionAction) => {
	if (action.id === 'other-actions-dropdown' || action.id === 'detach') {
		return false
	}
	if (action.variant === 'destructive') {
		return true
	}
	if (action.dropdown || (action.dropdownItems && action.dropdownItems.length > 0)) {
		return true
	}
	const lowerLabel = action.label.toLowerCase()
	return (
		lowerLabel.includes('delete') ||
		lowerLabel.includes('stop') ||
		lowerLabel.includes('detach') ||
		lowerLabel.includes('deactivate') ||
		lowerLabel.includes('remove') ||
		lowerLabel.includes('cancel') ||
		lowerLabel.includes('terminate') ||
		lowerLabel.includes('reboot') ||
		lowerLabel.includes('reset') ||
		lowerLabel.includes('clear') ||
		lowerLabel.includes('edit')
	)
}

const ResponsiveDropdown: React.FC<{
	id: string
	openDropdownId: string | null
	setOpenDropdownId: (id: string | null) => void
	actions: SelectionAction[]
	label: string
	className?: string
	onClearSelection: () => void
	dataTestId?: string
	setActivePopoverItem: (item: DropdownActionItem | null) => void
	setPopoverPosition: (position: PopoverPosition | null) => void
	setActivePopoverParentId: (id: string | null) => void
	setSelectedDropdownItem: (id: string | null) => void
}> = ({
	id,
	openDropdownId,
	setOpenDropdownId,
	actions,
	label,
	className = '',
	onClearSelection,
	dataTestId,
	setActivePopoverItem,
	setPopoverPosition,
	setActivePopoverParentId,
	setSelectedDropdownItem,
}) => {
	const open = openDropdownId === id
	const triggerRef = React.useRef<HTMLButtonElement>(null)

	const handleOpenChange = (newOpen: boolean) => {
		if (newOpen) {
			// If the dropdown is opening, close any active popover from this or other actions
			setActivePopoverItem(null)
			setPopoverPosition(null)
			setActivePopoverParentId(null)
			setSelectedDropdownItem(null)
			setOpenDropdownId(id)
		} else {
			if (openDropdownId === id) {
				setOpenDropdownId(null)
			}
		}
	}

	const handleComponentItemClick = (e: Event, item: DropdownActionItem, parentId: string) => {
		e.preventDefault()
		e.stopPropagation()

		if (item.disabled) {
			return
		}

		const targetElement = triggerRef.current || (e.currentTarget as HTMLElement)
		if (!targetElement) {
			return
		}
		const rect = targetElement.getBoundingClientRect()
		const position = computePopoverPosition(rect)

		// Close dropdown
		setOpenDropdownId(null)

		// Set selected dropdown item to the parentId so the sync effect doesn't close it
		setSelectedDropdownItem(parentId)

		// Open popover after dropdown closes
		setTimeout(() => {
			setPopoverPosition(position)
			setActivePopoverItem(item)
			setActivePopoverParentId(parentId)
		}, 150)
	}

	return (
		<DropdownMenu open={open} onOpenChange={handleOpenChange}>
			<DropdownMenuTrigger asChild>
				<Button
					ref={triggerRef}
					variant='ghost'
					size='sm'
					data-testid={dataTestId}
					className={`h-(--height-control) px-3 text-body font-medium text-foreground min-w-25 flex items-center justify-center border hover:bg-sidebar rounded-sm transform transition-transform duration-200 ease-in-out hover:scale-105 data-[state=open]:scale-105 data-[state=open]:bg-sidebar ${className}`}>
					{label}
					<ChevronDown className='ml-2 size-(--icon-standalone)' />
				</Button>
			</DropdownMenuTrigger>

			<DropdownMenuContent
				align='start'
				side='bottom'
				sideOffset={4}
				collisionPadding={20}
				avoidCollisions={true}
				className='w-auto min-w-50 z-[220]'>
				{actions.map(action => {
					if (action.dropdown && action.dropdownItems) {
						const subMenuTrigger = (
							<DropdownMenuSubTrigger
								disabled={action.disabled}
								data-testid={action.dataTestId}
								className={`flex items-center gap-2 cursor-pointer text-control! h-(--height-control) min-h-(--height-control) w-full ${
									action.disabled ? 'opacity-50 cursor-not-allowed' : ''
								}`}>
								{action.icon && (
									<span className={action.disabled ? '' : '[&_svg]:opacity-100 [&_svg]:text-foreground'}>
										{action.icon}
									</span>
								)}
								<span>{action.label}</span>
							</DropdownMenuSubTrigger>
						)

						const subMenu = (
							<DropdownMenuSub key={action.id}>
								<TooltipWrapper
									tooltip={action.tooltip}
									access={action.access}
									dataTestId={`${action.id}-tooltip`}
									wrapperClassName='block'>
									{subMenuTrigger}
								</TooltipWrapper>
								<DropdownMenuSubContent className='w-auto min-w-50 z-[230]'>
									{action.dropdownItems.map((item, index) => {
										const subItemNode = item.component ? (
											<DropdownMenuItem
												key={item.id}
												onSelect={e => {
													handleComponentItemClick(e, item, action.id)
												}}
												disabled={item.disabled}
												className='flex items-center gap-2 cursor-pointer text-control! h-(--height-control) min-h-(--height-control)'>
												{item.icon && <span>{item.icon}</span>}
												<span>{item.label}</span>
											</DropdownMenuItem>
										) : (
											<DropdownMenuItem
												key={item.id}
												onSelect={async () => {
													if (!item.disabled) {
														await item.onClick?.()
														onClearSelection()
														setOpenDropdownId(null)
													}
												}}
												disabled={item.disabled}
												className='flex items-center gap-2 cursor-pointer text-control! h-(--height-control) min-h-(--height-control)'>
												{item.icon && <span>{item.icon}</span>}
												<span>{item.label}</span>
											</DropdownMenuItem>
										)

										const itemWithTooltip = (
											<TooltipWrapper
												tooltip={item.tooltip}
												access={item.access}
												dataTestId={`${item.id}-tooltip`}
												wrapperClassName='block'>
												{subItemNode}
											</TooltipWrapper>
										)
										const contentNode = (
											<>
												{item.separator && index > 0 && <div className='h-px my-1' />}
												{itemWithTooltip}
											</>
										)

										return (
											<React.Fragment key={item.id}>
												{withAccessControlDropdownMenuItem(contentNode, item.access)}
											</React.Fragment>
										)
									})}
								</DropdownMenuSubContent>
							</DropdownMenuSub>
						)

						return (
							<React.Fragment key={action.id}>
								{withAccessControlDropdownMenuItem(subMenu, action.access)}
							</React.Fragment>
						)
					}

					if (action.component) {
						const menuItem = (
							<DropdownMenuItem
								key={action.id}
								onSelect={e => {
									handleComponentItemClick(e, action as unknown as DropdownActionItem, action.id)
								}}
								disabled={action.disabled}
								data-testid={action.dataTestId}
								className='flex items-center gap-2 cursor-pointer text-control! h-(--height-control) min-h-(--height-control)'>
								{action.icon && (
									<span className={action.disabled ? '' : '[&_svg]:opacity-100 [&_svg]:text-foreground'}>
										{action.icon}
									</span>
								)}
								<span>{action.label}</span>
							</DropdownMenuItem>
						)

						const itemWithTooltip = (
							<TooltipWrapper
								tooltip={action.tooltip}
								access={action.access}
								dataTestId={`${action.id}-tooltip`}
								wrapperClassName='block'>
								{menuItem}
							</TooltipWrapper>
						)

						return (
							<React.Fragment key={action.id}>
								{withAccessControlDropdownMenuItem(itemWithTooltip, action.access)}
							</React.Fragment>
						)
					}

					const regularItem = (
						<DropdownMenuItem
							key={action.id}
							variant={action.variant === 'destructive' ? 'destructive' : 'default'}
							onSelect={async () => {
								if (!action.disabled) {
									await action.onClick?.()
									onClearSelection()
									setOpenDropdownId(null)
								}
							}}
							disabled={action.disabled}
							data-testid={action.dataTestId}
							className='flex items-center gap-2 cursor-pointer text-control! h-(--height-control) min-h-(--height-control)'>
							{action.icon && (
								<span
									className={
										action.disabled
											? ''
											: action.variant === 'destructive'
												? '[&_svg]:opacity-100 [&_svg]:text-destructive'
												: '[&_svg]:opacity-100 [&_svg]:text-foreground'
									}>
									{action.icon}
								</span>
							)}
							<span>{action.label}</span>
						</DropdownMenuItem>
					)

					const itemWithTooltip = (
						<TooltipWrapper
							tooltip={action.tooltip}
							access={action.access}
							dataTestId={`${action.id}-tooltip`}
							wrapperClassName='block'>
							{regularItem}
						</TooltipWrapper>
					)

					return (
						<React.Fragment key={action.id}>
							{withAccessControlDropdownMenuItem(itemWithTooltip, action.access)}
						</React.Fragment>
					)
				})}
				{actions.length === 0 && (
					<DropdownMenuItem disabled className='text-control text-muted-foreground'>
						No actions available
					</DropdownMenuItem>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

const DropdownWithState: React.FC<{
	action: SelectionAction
	selectedDropdownItem: string | null
	setSelectedDropdownItem: (id: string | null) => void
	activePopoverItem: DropdownActionItem | null
	setActivePopoverItem: (item: DropdownActionItem | null) => void
	popoverPosition: PopoverPosition | null
	setPopoverPosition: (position: PopoverPosition | null) => void
	openDropdownId: string | null
	setOpenDropdownId: (id: string | null) => void
	activePopoverParentId: string | null
	setActivePopoverParentId: (id: string | null) => void
	onClearSelection: () => void
}> = ({
	action,
	selectedDropdownItem,
	setSelectedDropdownItem,
	activePopoverItem,
	setActivePopoverItem,
	setPopoverPosition,
	openDropdownId,
	setOpenDropdownId,
	activePopoverParentId,
	setActivePopoverParentId,
	onClearSelection,
}: {
	action: SelectionAction
	selectedDropdownItem: string | null
	setSelectedDropdownItem: (id: string | null) => void
	activePopoverItem: DropdownActionItem | null
	setActivePopoverItem: (item: DropdownActionItem | null) => void
	popoverPosition: PopoverPosition | null
	setPopoverPosition: (position: PopoverPosition | null) => void
	openDropdownId: string | null
	setOpenDropdownId: (id: string | null) => void
	activePopoverParentId: string | null
	setActivePopoverParentId: (id: string | null) => void
	onClearSelection: () => void
}) => {
	const buttonRef = React.useRef<HTMLButtonElement>(null)
	const isOpen = openDropdownId === action.id

	const handleItemClick = (item: DropdownActionItem) => {
		if (item.disabled) {
			return
		}
		if (activePopoverItem) {
			handleClosePopover()
			return
		}

		// Capture button position with smart positioning
		if (buttonRef.current) {
			const rect = buttonRef.current.getBoundingClientRect()
			setPopoverPosition(computePopoverPosition(rect))
		}
		setOpenDropdownId(null)
		setTimeout(() => {
			setActivePopoverItem(item)
			setActivePopoverParentId(action.id)
		}, 100)
	}

	const handleClosePopover = useCallback(() => {
		setActivePopoverItem(null)
		setPopoverPosition(null)
		setActivePopoverParentId(null)
	}, [setActivePopoverItem, setPopoverPosition, setActivePopoverParentId])

	// Determine the button label: show selected item label if a modal is open from this dropdown
	const isModalOpenFromThisDropdown = activePopoverItem && activePopoverParentId === action.id
	const displayLabel = isModalOpenFromThisDropdown ? activePopoverItem.label : action.label

	return (
		<>
			<TooltipWrapper tooltip={action.tooltip} access={action.access} dataTestId={`${action.id}-tooltip`}>
				<DropdownMenu open={isOpen} onOpenChange={newOpen => setOpenDropdownId(newOpen ? action.id : null)}>
					<DropdownMenuTrigger asChild>
						<Button
							ref={buttonRef}
							variant='ghost'
							size='sm'
							disabled={action.disabled}
							onClick={() => {
								// Close any active popover from this dropdown's items before opening
								if (activePopoverItem) {
									setActivePopoverItem(null)
									setPopoverPosition(null)
								}
								setSelectedDropdownItem(action.id)
							}}
							className={`h-(--height-control) px-3 text-body font-medium min-w-17.5 border hover:bg-sidebar rounded-sm transform transition-transform duration-200 ease-in-out hover:scale-105 data-[state=open]:scale-105 data-[state=open]:bg-sidebar ${
								action.variant === 'destructive' && !action.disabled
									? 'text-destructive hover:bg-destructive/10 hover:text-destructive data-[state=open]:bg-destructive/10'
									: 'text-foreground '
							} ${action.disabled ? 'opacity-50 cursor-not-allowed' : ''} ${selectedDropdownItem === action.id ? 'bg-sidebar' : ''}`}>
							{action.icon && (
								<span
									className={
										action.disabled
											? 'mr-2'
											: action.variant === 'destructive'
												? 'mr-2 [&_svg]:opacity-100 [&_svg]:text-destructive'
												: 'mr-2 [&_svg]:opacity-100 [&_svg]:text-foreground'
									}>
									{action.icon}
								</span>
							)}
							<span className='text-body'>{displayLabel}</span>
							<ChevronDown className='ml-2 size-(--icon-standalone)' />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						align='start'
						className='z-[220] cursor-pointer'
						collisionPadding={20}
						avoidCollisions={true}>
						{action.dropdownItems?.map((item, index) => {
							const menuItem = item.component ? (
								<DropdownMenuItem
									data-testid={item.dataTestId}
									onSelect={e => {
										e.preventDefault()
										handleItemClick(item)
									}}
									disabled={item.disabled}
									className='flex items-center gap-2 cursor-pointer text-control! h-(--height-control) min-h-(--height-control)'>
									{item.icon && (
										<span className={item.disabled ? '' : '[&_svg]:opacity-100 [&_svg]:text-foreground'}>
											{item.icon}
										</span>
									)}
									<span>{item.label}</span>
								</DropdownMenuItem>
							) : (
								<DropdownMenuItem
									data-testid={item.dataTestId}
									onSelect={async () => {
										if (!item.disabled) {
											await item.onClick?.()
											onClearSelection()
										}
									}}
									disabled={item.disabled}
									className='flex items-center gap-2 cursor-pointer text-control! h-(--height-control) min-h-(--height-control)'>
									{item.icon && (
										<span className={item.disabled ? '' : '[&_svg]:opacity-100 [&_svg]:text-foreground'}>
											{item.icon}
										</span>
									)}
									<span>{item.label}</span>
								</DropdownMenuItem>
							)

							const itemWithTooltip = (
								<TooltipWrapper
									tooltip={item.tooltip}
									access={item.access}
									dataTestId={`${item.id}-tooltip`}
									wrapperClassName='block'>
									{menuItem}
								</TooltipWrapper>
							)
							const contentNode = (
								<>
									{item.separator && index > 0 && <div className='h-px my-1' />}
									{itemWithTooltip}
								</>
							)
							return (
								<React.Fragment key={item.id}>
									{withAccessControlDropdownMenuItem(contentNode, item.access)}
								</React.Fragment>
							)
						})}
					</DropdownMenuContent>
				</DropdownMenu>
			</TooltipWrapper>
		</>
	)
}

export const SelectionToolbar: React.FC<SelectionToolbarProps> = ({
	selectedCount,
	onClearSelection,
	primaryActions = [],
	dropdownGroups = [],
	className = '',
	style = {},
	columnWidth = 10, // The width of a single column in pixels. If the toolbar spans multiple columns, provide the combined width (sum of the individual column widths) in pixels.
	selectedIds = [],
	selectedIdsLabel = 'ID',
	selectedSummaryText,
	anchorToFirstColumn = false,
	dataTestId,
	clearSelectionTestId,
	selectedCountTestId,
	preserveSelectionToolbarInBottomBar = false,
	tableContainerRef,
	splitActionsOnTablet = false,
}) => {
	const [selectedDropdownItem, setSelectedDropdownItem] = useState<string | null>(null)
	const [activePopoverItem, setActivePopoverItem] = useState<DropdownActionItem | null>(null)
	useSelectionPopoverHold(activePopoverItem !== null)
	const [popoverPosition, setPopoverPosition] = useState<PopoverPosition | null>(null)
	const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
	const [activePopoverParentId, setActivePopoverParentId] = useState<string | null>(null)

	// Add this ref for the popover
	const popoverRef = React.useRef<HTMLDivElement>(null)

	const [screenWidth, setScreenWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280))

	useEffect(() => {
		const handleResize = () => {
			setScreenWidth(window.innerWidth)
		}
		window.addEventListener('resize', handleResize)
		return () => {
			window.removeEventListener('resize', handleResize)
		}
	}, [])

	const isDesktop = screenWidth >= 1280
	const isLaptop = screenWidth >= 768 && screenWidth < 1280
	const isMobile = screenWidth < 768

	const convertedDropdownGroups = dropdownGroups.map(group => ({
		id: `group-${group.label}`,
		label: group.label,
		dropdown: true,
		dropdownItems: group.actions,
		className: group.className,
	}))

	const allActions = [...primaryActions, ...convertedDropdownGroups]

	const actionsGroup: SelectionAction[] = []
	const operationsGroup: SelectionAction[] = []

	for (const action of allActions) {
		if (isDestructiveOrDropdown(action)) {
			operationsGroup.push(action)
		} else {
			actionsGroup.push(action)
		}
	}

	if (allActions.length > 1 && (actionsGroup.length === 0 || operationsGroup.length === 0)) {
		const mid = Math.ceil(allActions.length / 2)
		actionsGroup.splice(0, actionsGroup.length, ...allActions.slice(0, mid))
		operationsGroup.splice(0, operationsGroup.length, ...allActions.slice(mid))
	}

	// Add click outside handler in SelectionToolbar
	React.useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Element

			// Ignore if Right Click (context menu) - standard browser behavior is separate
			if (event.button === 2) {
				return
			}

			// Don't close if clicking on select/radix elements or elements marked to prevent close
			if (shouldPreventClose(target)) {
				return
			}

			// Don't close if clicking inside the popover (if active)
			if (popoverRef.current?.contains(target as Node)) {
				return
			}

			// Don't close if clicking inside the toolbar itself
			if (containerRef.current?.contains(target as Node)) {
				return
			}

			// Close any active popover
			if (activePopoverItem) {
				setActivePopoverItem(null)
				setPopoverPosition(null)
				setActivePopoverParentId(null)
			}

			// Don't clear selection if clicking on the table itself (e.g. clicking another row)
			// The table handles its own selection logic
			if (preserveSelectionToolbarInBottomBar) {
				if (tableContainerRef?.current) {
					const safeZone = target.closest('[data-selection-safe-zone="true"]')
					if (safeZone && tableContainerRef.current.contains(safeZone)) {
						return
					}
				}
			} else {
				if (target.closest('[data-selection-safe-zone="true"]')) {
					return
				}
			}

			// Always clear selection when clicking elements explicitly marked to do so (e.g. Create CTAs)
			if (target.closest('[data-clears-selection="true"]')) {
				onClearSelection()
				return
			}

			// Don't clear selection if clicking on interactive elements (buttons, links, inputs, etc.)
			if (isInteractiveElement(target)) {
				if (preserveSelectionToolbarInBottomBar) {
					if (tableContainerRef?.current?.contains(target as Node)) {
						return
					}
				} else {
					return
				}
			}

			onClearSelection()
		}

		document.addEventListener('mousedown', handleClickOutside)

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [activePopoverItem, onClearSelection, preserveSelectionToolbarInBottomBar, tableContainerRef])

	// Sync active popover with selected dropdown item
	React.useEffect(() => {
		// If we switched to a different action or cleared selection,
		// and we have a popover open from a different parent, close it.
		if (selectedDropdownItem !== activePopoverParentId) {
			if (activePopoverItem) {
				setActivePopoverItem(null)
				setPopoverPosition(null)
				setActivePopoverParentId(null)
			}
		}
	}, [selectedDropdownItem, activePopoverParentId, activePopoverItem])

	// Adjust popover position to prevent right-edge viewport overflow
	React.useEffect(() => {
		if (!activePopoverItem || !popoverPosition || !popoverRef.current) {
			return
		}

		const element = popoverRef.current
		const adjustPosition = () => {
			const rect = element.getBoundingClientRect()
			const rightEdge = popoverPosition.x + rect.width
			const maxRight = window.innerWidth - VIEWPORT_PADDING
			if (rightEdge > maxRight) {
				const adjustedX = Math.max(VIEWPORT_PADDING, maxRight - rect.width)
				element.style.left = `${adjustedX}px`
			} else {
				element.style.left = `${popoverPosition.x}px`
			}
		}

		// Initial adjustment
		adjustPosition()

		// Monitor size changes (e.g. loading to loaded transitions)
		const observer = new ResizeObserver(() => {
			adjustPosition()
		})
		observer.observe(element)

		return () => {
			observer.disconnect()
		}
	}, [activePopoverItem, popoverPosition])

	const containerRef = React.useRef<HTMLDivElement>(null)

	if (selectedCount === 0) {
		return null
	}

	return (
		<div
			ref={containerRef}
			data-testid={dataTestId}
			className={`
        z-50 
        bg-card border border-border shadow-levitating rounded-sm
        flex items-center py-1 px-4 space-x-2 mt-0.5
        ${className}
      `}
			style={{
				left: isMobile ? '8px' : `${columnWidth}px`,
				...(anchorToFirstColumn ? { width: 'max-content', maxWidth: '100%' } : { minWidth: 'max-content' }),
				height: 'var(--height-contextual-menu)',
				...style,
			}}>
			{/* Selection count and clear button */}
			<div className='flex items-center space-x-3'>
				<Tooltip>
					<TooltipTrigger asChild>
						<div className='text-body font-medium text-foreground cursor-pointer'>
							<span data-testid={selectedCountTestId} className='text-body font-medium text-foreground'>
								Selected: {selectedCount}
							</span>
						</div>
					</TooltipTrigger>
					{selectedSummaryText ? (
						<TooltipContent side='bottom' sideOffset={8} className='max-w-sm'>
							<div>{selectedSummaryText}</div>
						</TooltipContent>
					) : (
						selectedIds.length > 0 && (
							<TooltipContent side='bottom' sideOffset={8} className='max-w-sm'>
								<div>
									<span className='font-semibold'>{selectedIdsLabel}: </span>
									{/* Cap rendered ids — select-all can hold 100k+, joining all janks the page */}
									<span className='wrap-break-word'>{selectedIds.slice(0, MAX_TOOLTIP_IDS).join(', ')}</span>
									{selectedCount > Math.min(selectedIds.length, MAX_TOOLTIP_IDS) && (
										<span className='text-muted-foreground'>
											{' '}
											+{selectedCount - Math.min(selectedIds.length, MAX_TOOLTIP_IDS)} more
										</span>
									)}
								</div>
							</TooltipContent>
						)
					)}
				</Tooltip>

				<Button
					variant='ghost'
					size='sm'
					onClick={() => {
						setActivePopoverItem(null)
						setPopoverPosition(null)
						setActivePopoverParentId(null)
						setSelectedDropdownItem(null)
						onClearSelection()
					}}
					className='size-(--height-control) p-0 text-body'
					aria-label='Clear selection'
					data-testid={clearSelectionTestId}>
					<X className='size-(--icon-standalone) text-foreground' />
				</Button>
			</div>

			{/* Desktop View */}
			{isDesktop && primaryActions.length > 0 && <div className='h-5 w-0.5 bg-border mr-4' />}
			{isDesktop && primaryActions.length > 0 && (
				<div className='flex items-center space-x-2 text-body'>
					{primaryActions.map(action => {
						if (action.component) {
							return (
								<React.Fragment key={action.id}>
									{withAccessControl(
										<SingleButtonPopoverWrapper
											action={action}
											selectedDropdownItem={selectedDropdownItem}
											setSelectedDropdownItem={setSelectedDropdownItem}
											setOpenDropdownId={setOpenDropdownId}
											onClearSelection={onClearSelection}
											setActivePopoverItem={setActivePopoverItem}
										/>,
										action.access,
									)}
								</React.Fragment>
							)
						}

						// Update the DropdownWithState call to pass these props
						if (action.dropdown && action.dropdownItems) {
							return (
								<React.Fragment key={action.id}>
									{withAccessControl(
										<DropdownWithState
											action={action}
											selectedDropdownItem={selectedDropdownItem}
											setSelectedDropdownItem={setSelectedDropdownItem}
											activePopoverItem={activePopoverItem}
											setActivePopoverItem={setActivePopoverItem}
											popoverPosition={popoverPosition}
											setPopoverPosition={setPopoverPosition}
											openDropdownId={openDropdownId}
											setOpenDropdownId={setOpenDropdownId}
											activePopoverParentId={activePopoverParentId}
											setActivePopoverParentId={setActivePopoverParentId}
											onClearSelection={onClearSelection}
										/>,
										action.access,
									)}
								</React.Fragment>
							)
						}

						// Regular button
						const regularButton = (
							<Button
								key={action.id}
								variant='ghost'
								size='sm'
								onClick={async () => {
									setActivePopoverItem(null)
									setSelectedDropdownItem(action.id)
									try {
										await action.onClick?.()
										onClearSelection()
									} catch (error) {
										console.error('Action failed:', error)
									} finally {
										setSelectedDropdownItem(null)
									}
								}}
								disabled={action.disabled}
								data-testid={action.dataTestId}
								className={`h-(--height-control) px-3 text-body font-medium min-w-17.5 border hover:bg-sidebar rounded-sm transform transition-transform duration-200 ease-in-out hover:scale-105 ${
									action.disabled
										? 'opacity-50 cursor-not-allowed'
										: action.variant === 'destructive'
											? 'text-destructive hover:bg-destructive/10 hover:text-destructive'
											: 'text-foreground'
								} ${selectedDropdownItem === action.id ? 'bg-sidebar' : ''}`}>
								{action.icon && (
									<span
										className={
											action.disabled
												? 'mr-2 text-body'
												: action.variant === 'destructive'
													? 'mr-2 text-body [&_svg]:opacity-100 [&_svg]:text-destructive'
													: 'mr-2 text-body [&_svg]:opacity-100 [&_svg]:text-foreground'
										}>
										{action.icon}
									</span>
								)}
								<span
									className={`text-body ${action.variant === 'destructive' && !action.disabled ? 'text-destructive' : ''}`}>
									{action.label}
								</span>
							</Button>
						)

						return (
							<TooltipWrapper
								key={action.id}
								tooltip={action.tooltip}
								access={action.access}
								dataTestId={`${action.id}-tooltip`}>
								{withAccessControl(regularButton, action.access)}
							</TooltipWrapper>
						)
					})}
				</div>
			)}

			{/* Multiple Dropdown Groups */}
			{isDesktop &&
				dropdownGroups.length > 0 &&
				dropdownGroups.map(({ label, actions, className: groupClass }) => (
					<React.Fragment key={label}>
						<div className='h-6 w-px bg-background' />
						<HoverDropdown actions={actions} label={label} className={groupClass} onClearSelection={onClearSelection} />
					</React.Fragment>
				))}

			{/* Laptop/Tablet View */}
			{isLaptop && allActions.length > 0 && <div className='h-5 w-0.5 bg-border mr-4' />}
			{isLaptop && splitActionsOnTablet && (
				<>
					{actionsGroup.length > 0 && (
						<ResponsiveDropdown
							id='laptop-actions'
							openDropdownId={openDropdownId}
							setOpenDropdownId={setOpenDropdownId}
							actions={actionsGroup}
							label='Actions'
							onClearSelection={onClearSelection}
							dataTestId='selection-actions-dropdown-trigger'
							setActivePopoverItem={setActivePopoverItem}
							setPopoverPosition={setPopoverPosition}
							setActivePopoverParentId={setActivePopoverParentId}
							setSelectedDropdownItem={setSelectedDropdownItem}
						/>
					)}
					{operationsGroup.length > 0 && (
						<ResponsiveDropdown
							id='laptop-operations'
							openDropdownId={openDropdownId}
							setOpenDropdownId={setOpenDropdownId}
							actions={operationsGroup}
							label='Operations'
							onClearSelection={onClearSelection}
							dataTestId='selection-operations-dropdown-trigger'
							setActivePopoverItem={setActivePopoverItem}
							setPopoverPosition={setPopoverPosition}
							setActivePopoverParentId={setActivePopoverParentId}
							setSelectedDropdownItem={setSelectedDropdownItem}
						/>
					)}
				</>
			)}
			{isLaptop && !splitActionsOnTablet && allActions.length > 0 && (
				<ResponsiveDropdown
					id='laptop-actions-combined'
					openDropdownId={openDropdownId}
					setOpenDropdownId={setOpenDropdownId}
					actions={allActions}
					label='Actions'
					onClearSelection={onClearSelection}
					dataTestId='selection-actions-dropdown-trigger'
					setActivePopoverItem={setActivePopoverItem}
					setPopoverPosition={setPopoverPosition}
					setActivePopoverParentId={setActivePopoverParentId}
					setSelectedDropdownItem={setSelectedDropdownItem}
				/>
			)}

			{/* Mobile View */}
			{isMobile && allActions.length > 0 && <div className='h-5 w-0.5 bg-border mr-4' />}
			{isMobile && allActions.length > 0 && (
				<ResponsiveDropdown
					id='mobile-actions'
					openDropdownId={openDropdownId}
					setOpenDropdownId={setOpenDropdownId}
					actions={allActions}
					label='Actions'
					onClearSelection={onClearSelection}
					dataTestId='selection-actions-dropdown-trigger'
					setActivePopoverItem={setActivePopoverItem}
					setPopoverPosition={setPopoverPosition}
					setActivePopoverParentId={setActivePopoverParentId}
					setSelectedDropdownItem={setSelectedDropdownItem}
				/>
			)}
			{activePopoverItem &&
				popoverPosition &&
				createPortal(
					<div
						ref={popoverRef}
						data-testid={activePopoverItem.popoverTestId}
						className='fixed z-240 bg-background border border-border shadow-xl rounded-md flex flex-col overflow-hidden'
						style={{
							left: `${popoverPosition.x}px`,
							...(popoverPosition.positionAbove
								? { bottom: `${window.innerHeight - popoverPosition.y}px` }
								: { top: `${popoverPosition.y}px` }),
							...(popoverPosition.maxHeight ? { maxHeight: `${popoverPosition.maxHeight}px` } : {}),
						}}>
						{React.isValidElement(activePopoverItem.component)
							? React.cloneElement(activePopoverItem.component as React.ReactElement<PopoverInjectedProps>, {
									onClose: (...args: unknown[]) => {
										const originalOnClose = (activePopoverItem.component as React.ReactElement<PopoverInjectedProps>)
											.props?.onClose
										if (typeof originalOnClose === 'function') {
											originalOnClose(...args)
										}
										setActivePopoverItem(null)
										setPopoverPosition(null)
										setActivePopoverParentId(null)
									},
									onSuccess: (...args: unknown[]) => {
										const originalOnSuccess = (activePopoverItem.component as React.ReactElement<PopoverInjectedProps>)
											.props?.onSuccess
										if (typeof originalOnSuccess === 'function') {
											originalOnSuccess(...args)
										}
										onClearSelection()
									},
								})
							: activePopoverItem.component}
					</div>,
					document.body,
				)}
		</div>
	)
}
